import { useEffect, useRef, useState } from "react";

import {
  workbenchToPolicy,
  usePersistedWorkbenchReducer,
  workbenchPolicyReducer,
  addCommonRolesAndPermissionsActions,
} from "../../src/workbenchLogic";
import { StarterPolicy } from "./types";
import { EnvData, Fact, typedIdsEqual } from "../../src/types";

export const useOnboardingPolicy = (
  selectedEnv: EnvData,
  state: StarterPolicy
) => {
  const { workbenchPolicy, dispatch } = usePersistedWorkbenchReducer(
    selectedEnv,
    workbenchPolicyReducer.bind(
      null,
      () => {},
      () => {}
    )
  );

  const initialized = useRef(false);

  useEffect(() => {
    if (!initialized.current) {
      dispatch({
        action: "batch",
        actions: [
          { action: "reset" },
          { action: "edit actor name", actor: 0, name: "User" },
          { action: "add resource", resource: "Item" },
          ...addCommonRolesAndPermissionsActions(0),
        ],
      });
      initialized.current = true;
    }
  }, [dispatch, state]);

  const savePolicy = async () => {
    try {
      await savePolicyToServer(
        selectedEnv,
        workbenchToPolicy(workbenchPolicy, { skipTests: true })
      );
    } catch (e) {
      console.error(`Error while saving policy: ${e}`);
    }
  };

  return { savePolicy, workbenchPolicy };
};

export const factsEqual = (f1: Fact, f2: Fact): boolean => {
  return (
    f1.predicate === f2.predicate &&
    f1.args.length === f2.args.length &&
    f1.args.every((arg, i) => typedIdsEqual(arg, f2.args[i]))
  );
};

export const useAddFact = (selectedEnv: EnvData) => {
  const [savingFact, setSavingFact] = useState<boolean>(false);

  const addFact = async (fact: Fact) => {
    try {
      setSavingFact(true);

      const resp = await fetch(
        `/web/environment/${selectedEnv.pub_id}/api/facts/`,
        {
          method: "POST",
          body: JSON.stringify(fact),
        }
      );
      if (!resp.ok) {
        if (resp.status === 400) {
          let body = await resp.json();
          throw (
            "Couldn't add this fact because it is invalid:\n" + body.message
          );
        } else {
          throw "There was a problem adding this fact. Please try again later.";
        }
      }
    } catch (e) {
      // In practice we expect this to be pretty rare, and most likely a screw-up on our part.
      console.error(`Error adding typed fact: ${e}`);
      throw e;
    } finally {
      setSavingFact(false);
    }
  };

  return { addFact, savingFact };
};

const savePolicyToServer = async (
  selectedEnv: { pub_id: string },
  policy: string
) => {
  const resp = await fetch(
    `/web/environment/${selectedEnv.pub_id}/api/policy/`,
    {
      method: "POST",
      body: JSON.stringify({
        src: policy,
      }),
    }
  );

  if (!resp.ok) {
    const err = await resp.json();
    throw err;
  }
};
